import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { ObjectParam, StringParam, NumberParam, useQueryParams } from 'use-query-params'
import jobSearchQuery from '../../../../graphql/queries/jobsSearch'
import QUERY_KEYS from '../../../../graphql/queryKeys'
import NormalLayoutContainer from '../../../shared/NormalLayoutContainer'
import PageTitle from '../../../../components/page-title'
import { Spinner } from '../../../shared/Spinner'
import JobsTableSearchForm from '../../../../components/jobs-search/JobsTableSearchForm'
import JobsTable from '../../../../components/jobs-search/JobsTable'
import useQuery from '../../../../hooks/useQuery'
import { captureErrorAndNotify } from '../../../../utilities/errorHandlers'
import { jobsSortColumnEnumMap } from '../../../../graphql/enums'
import notify from '../../../../utilities/notify'
import useMutation from '../../../../hooks/useMutation'
import exportJobs from '../../../../graphql/mutations/exportJobs'
import { buildJobApiSearchFields } from '../../../../components/jobs-search/utils'

const pageSize = 25

export default function HaulerJobsSearchContainer () {
  const [fakeCount, setFakeCount] = useState(null)
  const [jobsExporting, setJobsExporting] = useState(false)
  const { allClients, user } = useSelector(({ allClients, user: { user } }) => ({
    allClients, user
  }))

  const [queryParams, setQueryParams] = useQueryParams({
    sort: ObjectParam,
    pager: ObjectParam,
    customId: NumberParam,
    streetAddress: StringParam,
    city: StringParam,
    zipCode: StringParam,
    clientId: NumberParam,
    status: StringParam,
    universalSearch: StringParam
  })

  const searchParams = useMemo(() => {
    return {
      clientId: queryParams.clientId,
      customId: queryParams.customId,
      streetAddress: queryParams.streetAddress,
      city: queryParams.city,
      zipCode: queryParams.zipCode,
      status: queryParams.status
    }
  }, [queryParams])

  const apiSearchFields = useMemo(() => buildJobApiSearchFields({
    ...searchParams,
    universalSearch: queryParams.universalSearch
  }), [searchParams, queryParams.universalSearch])

  const setQueryParamsReplace = useCallback((params) => {
    setQueryParams(params, 'replace')
  }, [setQueryParams])

  const { data, isFetching } = useQuery([
    QUERY_KEYS.jobsSearch,
    user.haulerId,
    25,
    queryParams.sort,
    apiSearchFields,
    queryParams.pager
  ],
  jobSearchQuery,
  {
    enabled: Boolean(queryParams.sort),
    onError (error) {
      captureErrorAndNotify(error, 'Error fetching jobs')
    }
  })

  function onPageRequest (direction, cursor) {
    setQueryParamsReplace({ ...queryParams, pager: { cursor, direction, pageSize } })
    if (!fakeCount) return
    if (direction === 'before') {
      setFakeCount(prevFakeCount => ({ start: prevFakeCount.start - pageSize, end: prevFakeCount.end - pageSize }))
    }
    if (direction === 'after') {
      setFakeCount(prevFakeCount => ({ start: prevFakeCount.start + pageSize, end: prevFakeCount.end + pageSize }))
    }
  }

  function onSortChange (newSortColumn) {
    let newSortDirection = 'desc'
    if (newSortColumn === queryParams.sort.column) {
      newSortDirection = queryParams.sort.direction === 'asc' ? 'desc' : 'asc'
    }

    setFakeCount(null)
    setQueryParamsReplace({
      ...searchParams,
      universalSearch: queryParams.universalSearch,
      sort: { column: newSortColumn, direction: newSortDirection }
    })
  }

  const clearFormValues = () => {
    setFakeCount(null)
    setQueryParamsReplace({})
  }

  const handleJobsSearch = (values) => {
    setFakeCount(null)
    setQueryParamsReplace({
      universalSearch: values.universalSearch || undefined,
      customId: values.customId || undefined,
      streetAddress: values.streetAddress || undefined,
      city: values.city || undefined,
      zipCode: values.zipCode || undefined,
      clientId: values.clientId || undefined,
      status: values.status || undefined,
      sort: queryParams.sort
    })
  }

  useEffect(function setDefaultQueryParams () {
    const defaultQueryParams = {}
    if (!queryParams.sort) {
      defaultQueryParams.sort = { column: 'customId', direction: 'desc' }
    }
    setQueryParams(defaultQueryParams, 'replaceIn')
  }, [queryParams, setQueryParams])

  useEffect(function toggleFakePagination () {
    if (!data || isFetching || fakeCount !== null) return
    setFakeCount({ start: 1, end: Math.min(pageSize, data?.jobs.totalCount) })
  }, [data, isFetching, fakeCount, setFakeCount])

  const { mutate: exportJobsMutation } = useMutation(exportJobs, {
    onSuccess (data) {
      const email = data?.userEmail
      window.alert(`Export Complete! The export will be emailed to ${email} with a link to download.`)
    },
    onError () {
      notify('error', 'Error exporting jobs')
    },
    onSettled () {
      setJobsExporting(false)
    }
  })

  function handleExportJobs () {
    if (jobsExporting) {
      return
    }
    setJobsExporting(true)
    return exportJobsMutation({
      haulerId: user.haulerId,
      searchFields: apiSearchFields,
      sortColumn: jobsSortColumnEnumMap[queryParams.sort.column],
      sortDirection: queryParams.sort.direction.toUpperCase()
    })
  }

  return (
    <div>
      <NormalLayoutContainer>
        <div className='container-fluid'>
          <PageTitle>Search Jobs</PageTitle>
          <div className='row'>
            <div className='col-xs-12'>
              <div className='panel panel-default row'>
                <div className='panel-body'>
                  <JobsTableSearchForm
                    clients={allClients.clients}
                    handleSubmit={handleJobsSearch}
                    clearFormValues={clearFormValues}
                    initialFormVals={{
                      ...searchParams,
                      universalSearch: queryParams.universalSearch || ''
                    }}
                    handleExportJobs={handleExportJobs}
                    jobsExporting={jobsExporting}
                  />
                </div>
              </div>
            </div>
          </div>
          <Spinner isFetching={isFetching}>
            <div className='panel panel-default row'>
              <div className='panel-body'>
                {data?.jobs && queryParams.sort &&
                  <JobsTable
                    jobs={data.jobs}
                    sort={queryParams.sort}
                    onPageRequest={onPageRequest}
                    onSortChange={onSortChange}
                    fakeCount={fakeCount}
                  />}
              </div>
            </div>
          </Spinner>
        </div>
      </NormalLayoutContainer>
    </div>
  )
}
